import styled, { css, keyframes } from "styled-components";
import { markdownStyles } from "./markdownStyles";

const fadeIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

const pulse = keyframes`
	0%, 100% {
		opacity: 0.4;
	}
	50% {
		opacity: 1;
	}
`;

const bounce = keyframes`
	0%, 80%, 100% {
		transform: scale(0);
	}
	40% {
		transform: scale(1);
	}
`;

export const FloatingButtonContainer = styled.button`
	position: fixed;
	bottom: 24px;
	right: 24px;
	width: 56px;
	height: 56px;
	border-radius: 50%;
	background: rgb(80, 76, 154);
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 12px rgba(80, 76, 154, 0.4);
	transition: all 0.3s ease;
	z-index: 9998;

	&:hover {
		transform: scale(1.1);
		box-shadow: 0 6px 16px rgba(80, 76, 154, 0.5);
	}

	&:active {
		transform: scale(0.95);
	}

	svg {
		width: 28px;
		height: 28px;
		color: #fff;
	}
`;

export const ChatContainer = styled.div`
	position: fixed;
	bottom: 96px;
	right: 24px;
	width: 380px;
	height: 600px;
	max-height: calc(100vh - 120px);
	background: #fff;
	border-radius: 16px;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
	display: flex;
	flex-direction: column;
	overflow: hidden;
	z-index: 9999;
	animation: ${fadeIn} 0.3s ease;

	@media (max-width: 480px) {
		width: calc(100vw - 32px);
		height: calc(100vh - 120px);
		bottom: 96px;
		right: 16px;
	}
`;

export const ChatHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px;
	background: var(--Gray-900, #111827);
	color: #fff;
`;

export const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

export const AvatarSmall = styled.div`
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

export const HeaderInfo = styled.div`
	display: flex;
	flex-direction: column;
`;

export const HeaderTitle = styled.span`
	font-size: 18px;
	font-weight: 700;
`;

export const HeaderStatus = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 12px;
	color: #4ade80;
`;

export const StatusDot = styled.span`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: ${props =>
		props.status === "connected"
			? "#4ade80"
			: props.status === "connecting"
			? "#f59e0b"
			: "#ef4444"};
`;

export const CloseButton = styled.button`
	background: transparent;
	border: none;
	cursor: pointer;
	padding: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #fff;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.8;
	}

	svg {
		width: 24px;
		height: 24px;
	}
`;

export const ChatBody = styled.div`
	flex: 1;
	overflow-y: auto;
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 16px;
	background: #f9f9f9;

	&::-webkit-scrollbar {
		width: 6px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background: #ddd;
		border-radius: 3px;
	}
`;

export const WelcomeContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 32px 24px 24px;
`;

export const AvatarLarge = styled.div`
	width: 142px;
	height: 142px;
	border-radius: 50%;
	margin-bottom: 20px;
	border: 3px solid #111827;
	background-image: url(${props => props.bgImage});
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;
`;

export const WelcomeTitle = styled.h2`
	font-family: "Inter", sans-serif;
	font-size: 20px;
	font-weight: 700;
	color: #2d2d2d;
	line-height: 100%;
	letter-spacing: 0%;
	text-align: center;
	margin: 0 0 8px;
`;

export const WelcomeSubtitle = styled.p`
	font-family: "Inter", sans-serif;
	font-size: 14px;
	font-weight: 400;
	color: #666;
	line-height: 100%;
	letter-spacing: 0%;
	text-align: center;
	margin: 0 0 24px;
`;

export const WelcomeQuestion = styled.p`
	font-family: "Inter", sans-serif;
	font-size: 14px;
	font-weight: 700;
	color: #2d2d2d;
	line-height: 100%;
	letter-spacing: 0%;
	text-align: center;
	margin: 0 0 8px;
`;

export const WelcomeHint = styled.p`
	font-family: "Inter", sans-serif;
	font-size: 14px;
	font-weight: 400;
	color: #666;
	line-height: 100%;
	letter-spacing: 0%;
	text-align: center;
	margin: 0;
`;

export const QuickOptionsContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 12px;
	padding: 0 24px 24px;
`;

export const QuickOptionButton = styled.button`
	background: rgb(80, 76, 154);
	color: #fff;
	border: none;
	border-radius: 8px;
	padding: 10px 14px;
	font-family: "Inter", sans-serif;
	font-size: 12px;
	font-weight: 700;
	line-height: 100%;
	letter-spacing: 0%;
	text-align: center;
	cursor: pointer;
	transition: all 0.2s ease;
	white-space: normal;

	&:hover {
		background: rgb(71, 67, 136);
		transform: translateY(-1px);
	}

	&:active {
		transform: translateY(0);
	}
`;

export const MessagesContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

export const MessageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: ${props => (props.isUser ? "flex-end" : "flex-start")};
	gap: 4px;
`;

export const MessageBubble = styled.div`
	max-width: 85%;
	padding: 12px 16px;
	border-radius: 16px;
	font-size: 14px;
	line-height: 1.5;
	animation: ${fadeIn} 0.2s ease;

	${props =>
		props.isUser
			? css`
					background: rgb(80, 76, 154);
					color: #fff;
					border-bottom-right-radius: 4px;
			  `
			: css`
					background: #fff;
					color: #2d2d2d;
					border-bottom-left-radius: 4px;
					box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
			  `}
`;

export const MessageSender = styled.span`
	font-size: 12px;
	font-weight: 600;
	color: #666;
	margin-bottom: 2px;
`;

export const MessageContent = styled.div`
	word-break: break-word;
	font-family: ${markdownStyles.body.fontFamily};
	color: ${props => (props.isUser ? "#fff" : markdownStyles.body.color)};
	font-size: ${markdownStyles.body.fontSize}px;
	line-height: ${markdownStyles.body.lineHeight};

	${props =>
		props.isLink &&
		css`
			padding: 0;
		`}

	a {
		font-family: ${markdownStyles.link.fontFamily};
		color: ${props => (props.isUser ? "#fff" : markdownStyles.link.color)};
		text-decoration: underline;
	}

	p {
		margin: 0 0 ${markdownStyles.paragraph.marginBottom}px;
	}

	ul,
	ol {
		margin: 0 0 ${markdownStyles.bullet_list.marginBottom}px;
		padding-left: 20px;
	}

	li {
		margin-bottom: 4px;
	}

	strong {
		font-family: ${markdownStyles.strong.fontFamily};
		font-weight: ${markdownStyles.strong.fontWeight};
		color: ${props => (props.isUser ? "#fff" : markdownStyles.strong.color)};
	}

	em {
		font-family: ${markdownStyles.em.fontFamily};
		font-style: ${markdownStyles.em.fontStyle};
		color: ${props => (props.isUser ? "#fff" : markdownStyles.em.color)};
	}

	h1 {
		font-size: ${markdownStyles.heading1.fontSize}px;
		font-weight: ${markdownStyles.heading1.fontWeight};
		color: ${props => (props.isUser ? "#fff" : markdownStyles.heading1.color)};
		margin: ${markdownStyles.heading1.marginTop}px 0 ${markdownStyles.heading1.marginBottom}px;
	}

	h2 {
		font-size: ${markdownStyles.heading2.fontSize}px;
		font-weight: ${markdownStyles.heading2.fontWeight};
		color: ${props => (props.isUser ? "#fff" : markdownStyles.heading2.color)};
		margin: ${markdownStyles.heading2.marginTop}px 0 ${markdownStyles.heading2.marginBottom}px;
	}

	h3 {
		font-size: ${markdownStyles.heading3.fontSize}px;
		font-weight: ${markdownStyles.heading3.fontWeight};
		color: ${props => (props.isUser ? "#fff" : markdownStyles.heading3.color)};
		margin: ${markdownStyles.heading3.marginTop}px 0 ${markdownStyles.heading3.marginBottom}px;
	}

	h4 {
		font-size: ${markdownStyles.heading4.fontSize}px;
		font-weight: ${markdownStyles.heading4.fontWeight};
		color: ${props => (props.isUser ? "#fff" : markdownStyles.heading4.color)};
		margin: ${markdownStyles.heading4.marginTop}px 0 ${markdownStyles.heading4.marginBottom}px;
	}

	h5 {
		font-size: ${markdownStyles.heading5.fontSize}px;
		font-weight: ${markdownStyles.heading5.fontWeight};
		color: ${props => (props.isUser ? "#fff" : markdownStyles.heading5.color)};
		margin: ${markdownStyles.heading5.marginTop}px 0 ${markdownStyles.heading5.marginBottom}px;
	}

	h6 {
		font-size: ${markdownStyles.heading6.fontSize}px;
		font-weight: ${markdownStyles.heading6.fontWeight};
		color: ${props => (props.isUser ? "#fff" : markdownStyles.heading6.color)};
		margin: ${markdownStyles.heading6.marginTop}px 0 ${markdownStyles.heading6.marginBottom}px;
	}

	blockquote {
		background: ${markdownStyles.blockquote.backgroundColor};
		border-left: ${markdownStyles.blockquote.borderLeftWidth}px solid
			${markdownStyles.blockquote.borderLeftColor};
		padding: ${markdownStyles.blockquote.paddingVertical}px
			${markdownStyles.blockquote.paddingHorizontal}px;
		margin: 0 0 ${markdownStyles.blockquote.marginBottom}px;
		border-radius: 6px;
	}

	pre {
		background: ${markdownStyles.code_block.backgroundColor};
		padding: ${markdownStyles.code_block.padding}px;
		border-radius: ${markdownStyles.code_block.borderRadius}px;
		overflow-x: auto;
		margin: 0 0 ${markdownStyles.code_block.marginBottom}px;
		font-family: ${markdownStyles.code_block.fontFamily};
		color: ${props => (props.isUser ? "#fff" : markdownStyles.code_block.color)};
	}

	pre code {
		background: transparent;
		padding: 0;
		border-radius: 0;
		font-size: inherit;
		color: inherit;
	}

	code {
		background: ${markdownStyles.code_inline.backgroundColor};
		padding: 2px ${markdownStyles.code_inline.paddingHorizontal}px;
		border-radius: ${markdownStyles.code_inline.borderRadius}px;
		font-family: ${markdownStyles.code_inline.fontFamily};
		color: ${props => (props.isUser ? "#fff" : markdownStyles.code_inline.color)};
	}

	hr {
		border: none;
		height: ${markdownStyles.hr.height}px;
		background: ${markdownStyles.hr.backgroundColor};
		margin: ${markdownStyles.hr.marginVertical}px 0;
	}

	img {
		width: ${markdownStyles.image.width};
		border-radius: ${markdownStyles.image.borderRadius}px;
		margin: 0 0 ${markdownStyles.image.marginBottom}px;
		height: auto;
		display: block;
	}

	table {
		width: 100%;
		border-collapse: collapse;
		border: ${markdownStyles.table.borderWidth}px solid ${markdownStyles.table.borderColor};
		border-radius: ${markdownStyles.table.borderRadius}px;
		margin: 0 0 ${markdownStyles.table.marginBottom}px;
		overflow: hidden;
	}

	tr {
		border-bottom: ${markdownStyles.tr.borderBottomWidth}px solid
			${markdownStyles.tr.borderBottomColor};
	}

	th {
		padding: ${markdownStyles.th.padding}px;
		background: ${markdownStyles.th.backgroundColor};
		font-weight: ${markdownStyles.th.fontWeight};
		font-family: ${markdownStyles.th.fontFamily};
		color: ${props => (props.isUser ? "#fff" : markdownStyles.th.color)};
		text-align: left;
		border-right: 1px solid ${markdownStyles.table.borderColor};
	}

	td {
		padding: ${markdownStyles.td.padding}px;
		font-family: ${markdownStyles.td.fontFamily};
		color: ${props => (props.isUser ? "#fff" : markdownStyles.td.color)};
		border-right: 1px solid ${markdownStyles.table.borderColor};
	}

	th:last-child,
	td:last-child {
		border-right: none;
	}

	tbody tr:last-child {
		border-bottom: none;
	}
`;

export const LinkButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: #25d366;
	color: #fff;
	border: none;
	border-radius: 8px;
	padding: 12px 24px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	width: 100%;
	box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);

	&:hover {
		background: #20ba5a;
		transform: translateY(-1px);
		box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
	}

	&:active {
		transform: translateY(0);
	}
`;

export const FeedbackContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 8px;
`;

export const FeedbackText = styled.span`
	font-size: 12px;
	color: #888;
`;

export const FeedbackButton = styled.button`
	background: transparent;
	border: none;
	cursor: pointer;
	padding: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${props => (props.active ? "rgb(80, 76, 154)" : "#ccc")};
	transition: all 0.2s;

	&:hover {
		color: ${props => (props.active ? "rgb(80, 76, 154)" : "#888")};
		transform: scale(1.1);
	}

	svg {
		width: 18px;
		height: 18px;
	}
`;

export const TypingIndicatorContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 16px;
	background: #fff;
	border-radius: 16px;
	border-bottom-left-radius: 4px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
	max-width: 80px;
	animation: ${fadeIn} 0.2s ease;
`;

export const TypingDot = styled.span`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: rgb(80, 76, 154);
	animation: ${bounce} 1.4s infinite ease-in-out both;

	&:nth-child(1) {
		animation-delay: -0.32s;
	}

	&:nth-child(2) {
		animation-delay: -0.16s;
	}

	&:nth-child(3) {
		animation-delay: 0s;
	}
`;

export const ChatFooter = styled.div`
	display: flex;
	align-items: center;
	padding: 16px;
	background: #fff;
	border-top: 1px solid #eee;
	border-radius: 0 0 16px 16px;
`;

export const InputContainer = styled.div`
	flex: 1;
	display: flex;
	align-items: center;
	background: #f3f4f6;
	border-radius: 14px;
	padding: 8px 12px 8px 18px;
	transition: all 0.2s;
	gap: 8px;

	&:focus-within {
		background: #fff;
		box-shadow: 0 0 0 2px rgb(80, 76, 154);
	}
`;

export const ChatInput = styled.input`
	flex: 1;
	border: none;
	background: transparent;
	padding: 8px 0;
	font-size: 14px;
	color: #2d2d2d;
	outline: none;

	&::placeholder {
		color: #999;
	}
`;

export const SendButton = styled.button`
	background: transparent;
	border: none;
	cursor: pointer;
	padding: 6px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: rgb(80, 76, 154);
	transition: all 0.2s;
	flex-shrink: 0;
	margin-left: auto;

	&:hover:not(:disabled) {
		transform: scale(1.05);
	}

	&:disabled {
		color: rgb(80, 76, 154);
		opacity: 0.45;
		cursor: not-allowed;
		pointer-events: none;
	}

	svg {
		width: 24px;
		height: 24px;
	}
`;
